import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronsUpDown, LockIcon, LockOpenIcon } from "lucide-react";
import { useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";

/** The SDK types travelAgencyCompany as string, but the API returns an object at runtime. */
function companyDisplayName(company: unknown): string | null {
  if (typeof company === "string") {
    return company || null;
  }
  if (company && typeof company === "object" && "displayName" in company) {
    return (company as { displayName?: string | null }).displayName ?? null;
  }
  return null;
}

import adminAPI from "@/services/api";

import { useClaim } from "../api/use-claim";
import { formatInTimezone } from "../lib/dates";
import { type ClaimLegalStepName, legalStepDotClasses } from "../lib/legal-steps";
import { AbandonTypeBadge } from "./abandon-type-badge";

type ClaimGroup = Awaited<ReturnType<typeof adminAPI.listClaimGroups>>[number];
type GroupClaim = ClaimGroup["claimsGroupsAssociations"][number]["claim"];

/** Legal steps whose claims start with the group section collapsed (Vue DisplayClaimGroup). */
const STEPS_TO_COLLAPSE = ["UPCLAIM", "CONCILIATION", "COURT"];

/** DotLegalStep.vue — colored dot for a claim legal step. */
function DotLegalStep({ legalStepName }: { legalStepName: ClaimLegalStepName }) {
  const dot = legalStepDotClasses[legalStepName] ?? legalStepDotClasses.CANCELLED;
  return (
    <div className={cn("flex-none rounded-full p-1", dot.halo)}>
      <div className={cn("h-1.5 w-1.5 rounded-full", dot.core)} />
    </div>
  );
}

function GroupClaimCard({ groupClaim, isCurrent }: { groupClaim: GroupClaim; isCurrent: boolean }) {
  const firstPassenger = groupClaim.passengers[0];

  return (
    <div className="text-left">
      <Link
        to="/claims"
        search={{ claimId: groupClaim.id, claimTab: "overview" }}
        className={cn(
          "relative block cursor-pointer rounded-lg border bg-white px-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between",
          isCurrent ? "border-indigo-600 ring-2 ring-indigo-600" : "border-gray-300",
        )}
      >
        <div>
          <div className="flex items-center">
            <div className="h-10 w-10 flex-shrink-0">
              {groupClaim.effectiveAirlineIconURL ? (
                <img
                  className="h-10 w-10 rounded-full"
                  src={groupClaim.effectiveAirlineIconURL}
                  alt="Airline Logo"
                />
              ) : null}
            </div>
            {/* text-xs matches the Vue app's 12px body base (unclassed text there rendered at 12px). */}
            <div className="ml-4 divide-y-2 divide-transparent text-xs">
              <div className="text-sm font-medium text-gray-900">
                {groupClaim.effectiveFlightIdentifier} · {groupClaim.effectiveAirlineName}
              </div>
              <div className="font-medium text-slate-700">
                Vol {groupClaim.departureAirportIATA} → {groupClaim.arrivalAirportIATA} (du{" "}
                {formatInTimezone(
                  new Date(groupClaim.flightScheduledDepartureUTC),
                  groupClaim.departureAirportTimezone,
                  "dd/MM/yyyy",
                )}
                )
              </div>
              {companyDisplayName(groupClaim.travelAgencyCompany) ? (
                <div>
                  <span className="mr-2 text-slate-700">
                    Entreprise : {companyDisplayName(groupClaim.travelAgencyCompany)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <span className="flex items-center">
          <span className="flex flex-col text-sm">
            <span className="text-xs font-medium text-gray-900">
              {firstPassenger ? (
                <>
                  {firstPassenger.firstName} <span className="uppercase">{firstPassenger.lastName}</span>
                  {groupClaim.passengers.length > 1 ? (
                    <Tooltip>
                      <TooltipTrigger render={<span className="ml-1" />}>
                        (+ {groupClaim.passengers.length - 1}pax.)
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="space-y-0.5">
                          {groupClaim.passengers
                            .filter((_, index) => index)
                            .map((passenger) => (
                              <div key={`${passenger.firstName}-${passenger.lastName}`}>
                                {passenger.firstName} {passenger.lastName?.toUpperCase()}
                              </div>
                            ))}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </>
              ) : null}
            </span>
            <span className="text-gray-500">
              <span className="mt-1 inline-flex items-center text-xs">
                <DotLegalStep legalStepName={groupClaim.claimLegalStepName} />
                <span className="ml-1 block min-w-full sm:inline">{groupClaim.claimStateDisplayName}</span>
              </span>
            </span>
            {groupClaim.abandonType ? (
              <span className="mt-1 text-xs">
                <AbandonTypeBadge abandonType={groupClaim.abandonType} />
              </span>
            ) : null}
          </span>
        </span>
        <span className="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
          <span className="font-medium text-gray-900">#{groupClaim.ticketNumber}</span>
        </span>
      </Link>
    </div>
  );
}

/** Port of claim/DisplayClaimGroup.vue (display only — the drag & drop re-grouping is not ported). */
function DisplayClaimGroup({
  group,
  currentClaimId,
  defaultExpanded,
}: {
  group: ClaimGroup;
  currentClaimId: string;
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="mt-2 p-2">
      <div className="flex items-center justify-between p-2">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="inline-flex cursor-pointer items-center text-sm font-medium leading-6 text-gray-900"
        >
          <ChevronsUpDown className="h-5 w-5" />
          <span className="font-normal text-gray-700">Dossiers liés :&nbsp;</span>
          {group.groupName} · ({group.claimsGroupsAssociations.length})
        </button>
        <div className="inline-flex items-center">
          <a
            href={`/claims?claim%5Bquery%5D=${group.id}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium leading-6 text-indigo-600 hover:text-indigo-500"
          >
            Voir le groupe
          </a>
          <span className="mx-2">·</span>
          <span className="h-5 w-5 text-indigo-700">
            {group.lockedAt ? <LockIcon className="h-5 w-5" /> : <LockOpenIcon className="h-5 w-5" />}
          </span>
        </div>
      </div>

      {expanded ? (
        <div className="col-span-3 w-full divide-y-8 divide-transparent rounded-md px-2 py-4">
          {group.claimsGroupsAssociations.map((association) => (
            <GroupClaimCard
              key={association.id}
              groupClaim={association.claim}
              isCurrent={association.claim.id === currentClaimId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Port of claim/ClaimGroups.vue — groups/vagues the claim belongs to. */
export function ClaimGroups({ claimId }: { claimId: string }) {
  const { data: claim } = useClaim(claimId);

  const { data: groups = [] } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "groups"],
    queryFn: () => adminAPI.listClaimGroups(claimId),
  });

  if (!groups.length) {
    return null;
  }

  const defaultExpanded = claim ? !STEPS_TO_COLLAPSE.includes(claim.claimLegalStep.name) : true;

  return (
    <div className="border-y bg-stone-50 p-2">
      <div className={cn("w-full sm:px-0", groups.length > 1 && "py-1")}>
        {groups.length > 1 ? (
          /* Tab values must be unique — two same-named groups sharing a value make Base UI's
             highlighted-tab tracking ping-pong into "Maximum update depth". Key by id. */
          <Tabs defaultValue={String(groups[0]?.id)} className="w-full py-5">
            <TabsList>
              {groups.map((group) => (
                <TabsTrigger key={group.id} value={String(group.id)}>
                  {group.groupName} ({group.claimsGroupsAssociations.length})
                </TabsTrigger>
              ))}
            </TabsList>
            {groups.map((group) => (
              <TabsContent key={group.id} value={String(group.id)}>
                <DisplayClaimGroup group={group} currentClaimId={claimId} defaultExpanded={defaultExpanded} />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <DisplayClaimGroup group={groups[0]} currentClaimId={claimId} defaultExpanded={defaultExpanded} />
        )}
      </div>
    </div>
  );
}
